import {
  Box,
  Flex,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { t } from '@lingui/core/macro';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { SortableHeader } from '@duke-hq/ui/components/SortableHeader/SortableHeader';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import type { SortFilters } from './SitesLocations';
import { states } from '@duke-hq/svc-api/lib/states';
import { LocationError } from './LocationError';
import { SortOrder } from '@duke-hq/lib/pagination';

type SortPropsInterface = SortFilters & {
  available?: string | undefined;
  page?: number | undefined;
};

type SiteLocationItemType =
  RouterOutputs['providers']['locations']['listSiteLocationsWithPagination']['items'][number];

export function SitesLocationsTable({
  sites,
  urlParams,
  onFilters,
}: {
  sites: SiteLocationItemType[];
  urlParams: SortPropsInterface;
  onFilters: (filters: SortPropsInterface, sort: boolean) => void;
}) {
  const { providerId } = useParams<{ providerId: string }>();
  const { available, sort, orderBy } = urlParams;
  const toast = useToast();
  const utils = trpcAdmin.useUtils();

  const handleSort = useCallback(
    (name: string) => {
      const newSortDirection = sort === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
      const updatedFilters = {
        orderBy: name,
        sort: newSortDirection,
        available: available ?? undefined,
      } as SortPropsInterface;

      onFilters({ ...updatedFilters }, true);
    },
    [onFilters, available, sort],
  );

  if (providerId == null) {
    return <LocationError message={t`An error occurred, providerId is required.`} />;
  }

  const updateSiteLocation = trpcAdmin.providers.locations.updateSiteLocation.useMutation({
    onSuccess: () => {
      toast({
        title: t`Site location updated successfully.`,
        status: 'success',
        duration: 7000,
        isClosable: true,
      });

      void utils.providers.locations.listSiteLocationsWithPagination.refetch();
      void utils.providers.locations.countAvailableSiteLocations.refetch();
    },
  });

  const onEdit = async (site: SiteLocationItemType) => {
    await updateSiteLocation.mutateAsync({
      providerId,
      id: site.id,
      data: {
        id: site.id,
        name: site.name,
        state: site.state,
        country: site.country,
        available: site.available,
      },
    });
  };

  return (
    <Table>
      <Thead>
        <Tr>
          <Th>
            <SortableHeader
              label={t`Name`}
              columnName="name"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
          <Th>
            <Text>{t`Consultants`}</Text>
          </Th>
          <Th>
            <Text>{t`Enrolments`}</Text>
          </Th>
          <Th>
            <SortableHeader
              label={t`Available`}
              columnName="available"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
        </Tr>
      </Thead>
      <Tbody>
        {Array.isArray(sites) && sites.length !== 0
          ? sites.map((site: SiteLocationItemType) => (
              <TableRow key={site.id} site={site} onEdit={onEdit} />
            ))
          : null}
      </Tbody>
    </Table>
  );
}

function TableRow({
  site,
  onEdit,
}: {
  site: SiteLocationItemType;
  onEdit: (site: SiteLocationItemType) => void;
}) {
  const handleSwitchChange = useCallback(async () => {
    site.available = !site.available;
    const updatedSite = {
      ...site,
    };
    onEdit(updatedSite);
  }, [site]);

  return (
    <Tr key={site.id}>
      <Td>
        <Flex direction="column">
          <Box>
            <Text fontWeight="medium">{site.name}</Text>
          </Box>
          {site.state ? (
            <Flex align="center" color="fg.muted" gap={1}>
              <Text>{states[site.state]}</Text>
            </Flex>
          ) : null}
        </Flex>
      </Td>
      <Td>{site.totalConsultants}</Td>
      <Td>{site.totalEnrolments}</Td>
      <Td>
        <Switch
          colorScheme="brand"
          size="md"
          key={site.id}
          isChecked={site.available}
          onChange={handleSwitchChange}
        />
      </Td>
    </Tr>
  );
}
